import { FC, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';

import { featureFlags } from 'src/config/featureFlags';
import { useHasPermission } from 'src/use-cases/user/useUserPermissions';
import { PermissionType } from 'src/types/permission';
import { LandingSectionName } from 'src/presentations/components/organisms/landing/types';
import { useUserContext } from 'src/use-cases/user/userDetails';

// Import custom icon components
import {
  IconPresentionChart,
  IconPresentionChartFilled,
  IconReceiptMinus,
  IconReceiptMinusFilled,
  IconStar,
  IconStarFilled,
  IconCardEdit,
  IconCardEditFilled,
  IconSetting2,
  IconTLogo,
  IconFlash,
  IconHelp,
  IconHelpFilled,
} from 'src/presentations/components/atom/icons';

import { useTourContext } from 'src/presentations/guided-tours';
import FreyaChatPopover from 'src/presentations/components/organisms/askFreya/FreyaChatPopover';
import { useFreyaPopover } from 'src/presentations/components/organisms/askFreya/useFreyaPopover';
import { SidebarNavProps } from './types';
import IconWrapper from './IconWrapper';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  isVisible?: boolean;
  badge?: React.ReactNode;
  onClick?: (event: React.MouseEvent) => void;
}

const SidebarNav: FC<SidebarNavProps> = ({ collapsed, setCollapsed, isLandingPage = false }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { hasPermission: hasAuditPermission } = useHasPermission(PermissionType.AUDIT);
  const { hasPermission: hasPerformancePermission } = useHasPermission(PermissionType.PERFORMANCE);
  const { hasPermission: hasReportPermission } = useHasPermission(PermissionType.REPORT);
  const { hasPermission: hasRateCardPermission } = useHasPermission(PermissionType.AUDIT);
  const { userDetails } = useUserContext();
  const { isOpen: freyaPopoverOpen, openPopover, closePopover } = useFreyaPopover();

  const userRoles = userDetails?.roles ?? [];
  const isCreditViewerRole = userRoles.includes('CREDIT_VIEWER');

  const isActive = useCallback(
    (path: string) => {
      // For landing page sidebar, match by section parameter
      if (isLandingPage && location.pathname === '/') {
        const pathParams = new URLSearchParams(path.split('?')[1] || '');
        const currentParams = new URLSearchParams(location.search);
        const pathSection = pathParams.get('section');
        const currentSection = currentParams.get('section');

        // Special case: if we're on the root path with no section, highlight Audit
        if (!currentSection && pathSection === LandingSectionName.AUDIT) {
          return true;
        }

        // Check if the current section matches this menu item's section
        return pathSection === currentSection;
      }

      // Default behavior for other paths
      return location.pathname === path || (path !== '/' && location.pathname.startsWith(`${path}/`));
    },
    [isLandingPage, location]
  );

  const toggleCollapse = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    // Save to localStorage
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
  };

  const handleItemClick = (path: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
    }

    if (isLandingPage && path.startsWith('/?section=')) {
      const sectionParam = path.split('section=')[1];

      localStorage.setItem('lastSectionName', sectionParam);

      // Only update the URL parameters without reload
      const url = new URL(window.location.href);
      url.searchParams.set('section', sectionParam);
      window.history.pushState({}, '', url.toString());

      // Dispatch URL change event so components can react
      const popStateEvent = new PopStateEvent('popstate', { state: {} });
      window.dispatchEvent(popStateEvent);
      return;
    }

    // For detail page navigation, also save the current section if coming from landing
    if (location.pathname === '/' && location.search.includes('section=')) {
      const currentSection = new URLSearchParams(location.search).get('section');
      if (currentSection) {
        localStorage.setItem('lastSectionName', currentSection);
      }
    }

    // If no special handling needed, navigate to the path directly
    navigate(path);
  };

  // Create a reusable function to render menu items to avoid code duplication
  const renderMenuItem = (item: MenuItem) => {
    // Skip rendering if the item should not be visible
    if (item.isVisible === false) return null;

    const isAskFreya = item.path === '/ask-freya';
    const isItemActive = isAskFreya ? freyaPopoverOpen : isActive(item.path);

    const getLinkClasses = () => {
      const baseClasses = 'w-full px-12 group text-left flex items-center py-1.5 min-h-[48px] rounded-12 transition-all duration-300 ease-in-out';

      if (isAskFreya) {
        // No border on inner element — gradient border is handled by the wrapper div
        if (isItemActive) {
          return `${baseClasses} bg-[#373E43]`;
        }
        return `${baseClasses} bg-[#1C2429] hover:bg-[#373E43]`;
      }

      const defaultBase = `${baseClasses} border`;
      if (!isItemActive) {
        return `${defaultBase} bg-transparent border-transparent hover:bg-[#373E43] hover:border-[#484D4F]`;
      }

      return `${defaultBase} bg-[#373E43] border-[#484D4F]`;
    };

    const getWrapperStyle = (): { className: string; style: React.CSSProperties } => {
      if (!isAskFreya) {
        return { className: '', style: {} };
      }

      if (isItemActive) {
        return {
          className: 'rounded-12 p-px',
          style: {
            background: 'linear-gradient(272deg, #64CB7E 46.69%, #0CC5B9 91.41%)',
          },
        };
      }

      return {
        className: 'rounded-12 p-px freya-border-wrapper',
        style: {},
      };
    };

    const getIconColorClass = () => {
      if (isAskFreya) {
        // Ask Freya icon doesn't change color on hover
        return isItemActive ? 'text-[#00A651]' : 'text-white/70';
      }
      if (!isItemActive) return 'text-white/70 group-hover:text-primary';
      return 'text-primary';
    };

    const content = (
      <>
        <span className={`${collapsed ? 'w-full flex justify-center' : 'w-8 min-w-[40px]'} ${getIconColorClass()} transition-all duration-300 ease-in-out`}>
          {item.icon}
        </span>
        <div className='flex-1 overflow-hidden flex items-center'>
          <span
            className={`font-medium ${
              isItemActive ? 'text-white' : 'text-white/70'
            } text-xs xl:text-sm transition-all duration-300 whitespace-nowrap overflow-hidden ${
              collapsed ? 'invisible opacity-0 w-0' : 'visible opacity-100'
            }`}
          >
            {item.label}
          </span>
          {item.badge && !collapsed && <div className='ml-8'>{item.badge}</div>}
        </div>
      </>
    );

    const wrapper = getWrapperStyle();

    return (
      <li key={item.id} className='p-0'>
        <div className={wrapper.className} style={wrapper.style}>
          {item.onClick ? (
            <button id={item.id} type='button' onClick={item.onClick} className={getLinkClasses()}>
              {content}
            </button>
          ) : (
            <a id={item.id} href={item.path} onClick={(e) => handleItemClick(item.path, e)} className={getLinkClasses()}>
              {content}
            </a>
          )}
        </div>
      </li>
    );
  };

  const tour = useTourContext().appTour.registerTour('sidebar');

  const mainMenuItems: MenuItem[] = useMemo(
    () => [
      {
        id: tour.registerStep('audit', [], hasAuditPermission),
        label: 'Audit Exceptions',
        icon: <IconWrapper Icon={IconReceiptMinus} HoverIcon={IconReceiptMinusFilled} isActive={isActive(`/?section=${LandingSectionName.AUDIT}`)} />,
        path: `/?section=${LandingSectionName.AUDIT}`,
        isVisible: hasAuditPermission,
      },
      {
        id: tour.registerStep('performance', [], hasPerformancePermission),
        label: 'Performance Assessment',
        icon: <IconWrapper Icon={IconStar} HoverIcon={IconStarFilled} isActive={isActive(`/?section=${LandingSectionName.PERFORMANCE}`)} />,
        path: `/?section=${LandingSectionName.PERFORMANCE}`,
        isVisible: hasPerformancePermission,
      },
      {
        id: tour.registerStep('analytic', [], hasReportPermission),
        label: 'Reports & Analytics',
        icon: <IconWrapper Icon={IconPresentionChart} HoverIcon={IconPresentionChartFilled} isActive={isActive(`/?section=${LandingSectionName.ANALYTIC}`)} />,
        path: `/?section=${LandingSectionName.ANALYTIC}`,
        isVisible: hasReportPermission,
      },
      {
        id: tour.registerStep('rateCard', [], hasRateCardPermission),
        label: 'Rate Card Manager',
        icon: <IconWrapper Icon={IconCardEdit} HoverIcon={IconCardEditFilled} isActive={isActive(`/?section=${LandingSectionName.RATECARD}`)} />,
        path: `/?section=${LandingSectionName.RATECARD}`,
        isVisible: hasRateCardPermission,
      },
      {
        id: tour.registerStep('askFreya', [], featureFlags?.SHOW_ASK_FREYA),
        label: 'Ask Freya',
        icon: <IconFlash size={28} color='currentColor' />,
        path: '/ask-freya',
        isVisible: featureFlags?.SHOW_ASK_FREYA,
        badge: (
          <span className='bg-[#344054] text-[#D0D5DD] border border-[#475467] text-[10px] leading-3 font-medium px-5 py-0.5 rounded-full hidden'>Beta</span>
        ),
      },
    ],
    [isLandingPage, hasAuditPermission, hasPerformancePermission, hasReportPermission, hasRateCardPermission, location, freyaPopoverOpen]
  );

  const otherMenuItems = useMemo(() => {
    const internalMenu = {
      id: tour.registerStep('internalDashboard', [], userDetails?.systemAdmin),
      label: 'Internal Dashboard',
      icon: <IconTLogo size={24} color='currentColor' />,
      path: '/internal-dashboard',
    };

    const helpMenu = {
      id: tour.registerStep('helpCenter', [], (userDetails?.systemAdmin || isCreditViewerRole) && featureFlags?.GUIDED_TOURS),
      label: 'Help Center',
      icon: <IconWrapper Icon={IconHelp} HoverIcon={IconHelpFilled} isActive={isActive('/help-center')} />,
      path: '/help-center',
      isVisible: featureFlags?.GUIDED_TOURS,
    };

    const settingsMenu = {
      id: tour.registerStep('settings', [], userDetails?.systemAdmin || isCreditViewerRole),
      label: 'Settings',
      icon: <IconSetting2 size={24} color='currentColor' />,
      path: '/settings',
    };

    if (userDetails?.systemAdmin) {
      return [internalMenu, helpMenu, settingsMenu];
    }
    if (isCreditViewerRole) {
      return [settingsMenu, helpMenu];
    }

    return [];
  }, [userDetails, isCreditViewerRole]);

  return (
    <>
      <style>{`
        .freya-border-wrapper {
          background: transparent;
          transition: background 0.3s ease-in-out;
        }
        .freya-border-wrapper:hover {
          background: linear-gradient(272deg, #64CB7E 46.69%, #0CC5B9 91.41%);
        }
      `}</style>
      <div
        className={`${
          collapsed ? 'w-[72px]' : 'w-[240px] xl:w-[260px]'
        } h-full pt-24 pb-14 bg-[#1C2429] text-white transition-all duration-300 ease-in-out overflow-hidden flex flex-col rounded-12`}
      >
        <div id={tour.registerStep('menuHeader')} className='flex items-center justify-between border-b border-white/10 pb-16 px-24'>
          <div className='overflow-hidden'>
            <span
              className={`font-medium text-12 xl:text-14 text-white/40 transition-all duration-300 ${
                collapsed ? 'invisible opacity-0 w-0' : 'visible opacity-100'
              }`}
            >
              MENU
            </span>
          </div>
          <button
            id={tour.registerStep('sidebarToggle', [collapsed])}
            type='button'
            onClick={toggleCollapse}
            className='cursor-pointer flex items-center justify-center text-white/40 ml-auto'
          >
            {collapsed ? <MenuIcon /> : <KeyboardArrowLeftIcon />}
          </button>
        </div>

        <ul className='py-12 px-12 list-none'>{mainMenuItems.map(renderMenuItem)}</ul>

        {/* Show other items only for system admins */}
        {otherMenuItems?.length > 0 && (
          <div className='mt-auto'>
            <div className='h-px bg-white/10 my-2' />

            {!collapsed && (
              <div className='pt-12 px-24'>
                <span className='text-white/50 text-xs font-medium'>OTHERS</span>
              </div>
            )}

            <ul className='pt-12 px-12 list-none flex flex-col gap-[2px]'>{otherMenuItems.map(renderMenuItem)}</ul>
          </div>
        )}
      </div>
      {featureFlags?.SHOW_ASK_FREYA && <FreyaChatPopover />}
    </>
  );
};

// Use named export for consistency
export { SidebarNav };
